import { isEqual } from 'lodash'
import { Key, useCallback, useEffect, useRef, useState } from 'react'
import { BaseQueryFn, TypedUseLazyQuery } from '@reduxjs/toolkit/query/react'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { useDeepCompareDidUpdate } from '../useDeepCompareEffect'

export type LazyQueryHook<TResult, TArg> = TypedUseLazyQuery<
  TResult,
  TArg,
  BaseQueryFn<unknown, unknown, FetchBaseQueryError>
>

type GetArgsCallbackType<TResult, TArg> = (
  currentData: TResult,
  currentArg: TArg,
) => TArg | null

type ArrayItem<T> = T extends (infer U)[] ? U : never

type DefaultItem = {
  id?: string | number
}

const getItemId = (item: unknown): Key | undefined => {
  return (item as DefaultItem)?.id
}

const hasUniqueIds = <T,>(
  getItemKey: (item: T) => Key | undefined,
  items: T[],
): boolean => {
  if (items.length === 0) return false
  const keys = items.map(getItemKey)
  return keys.every((key) => key !== undefined)
}

const mergeItems = <T,>(
  getItemKey = getItemId,
  prevItems: T[] | undefined,
  newItems: T[],
  isNewPage: boolean,
): T[] => {
  if (!prevItems || prevItems.length === 0) {
    return newItems
  }

  if (!hasUniqueIds(getItemKey, newItems)) {
    return isNewPage ? [...prevItems, ...newItems] : newItems
  }

  if (isNewPage) {
    const existingKeys = new Set(prevItems.map(getItemKey))
    const uniqueNewItems = newItems.filter(
      (item) => !existingKeys.has(getItemKey(item)),
    )
    return [...prevItems, ...uniqueNewItems]
  }

  return prevItems.map((prevItem) => {
    const prevKey = getItemKey(prevItem)
    const updatedItem = newItems.find(
      (newItem) => getItemKey(newItem) === prevKey,
    )
    return updatedItem || prevItem
  })
}

type UseInfiniteLazyQueryProps<
  TResult,
  TArg,
  TWithLocalState extends boolean = false,
> = {
  useLazyQueryHook: LazyQueryHook<TResult, TArg>
  initialData?: TResult
  initialArg: TArg
  getNextArg: GetArgsCallbackType<TResult, TArg>
  withLocalState?: TWithLocalState
  skip?: boolean
} & (TWithLocalState extends false
  ? { getItemKey?: never }
  : { getItemKey?: (item: ArrayItem<TResult>) => Key })

type UseInfiniteLazyQueryReturn<TResult, TArg> = {
  error?: unknown
  isLoading: boolean
  isFetchingNext: boolean
  fetchNextPage: () => Promise<void>
  hasNextPage: boolean
  fetchPage: (
    getArgsCallback: GetArgsCallbackType<TResult, TArg>,
  ) => Promise<void>
  reset: () => void
}

type UseInfiniteLazyQueryLocalReturn<TResult, TArg> =
  UseInfiniteLazyQueryReturn<TResult, TArg> & {
    items?: TResult
  }

function useInfiniteLazyQuery<
  TResult extends unknown[],
  TArg,
  TWithLocalState extends boolean = false,
>({
  useLazyQueryHook,
  initialData,
  initialArg,
  getItemKey,
  getNextArg,
  withLocalState,
  skip,
}: UseInfiniteLazyQueryProps<
  TResult,
  TArg,
  TWithLocalState
>): TWithLocalState extends false
  ? UseInfiniteLazyQueryReturn<TResult, TArg>
  : UseInfiniteLazyQueryLocalReturn<TResult, TArg> {
  const hasInitialData = Boolean(initialData?.length)

  const [items, setItems] = useState<TResult | undefined>(undefined)
  const [hasNextPage, setHasNextPage] = useState(true)

  const args = useRef<TArg[]>([initialArg])
  const isTriggered = useRef<boolean>(false)
  const abortRef = useRef<() => void>()
  const pendingFetchRef = useRef<boolean>(false)
  const processedDataRef = useRef<TResult | null>(null)

  const getNextArgRef = useRef(getNextArg)
  getNextArgRef.current = getNextArg

  const currentArg = args.current.at(-1)

  const [_trigger, result] = useLazyQueryHook()
  const {
    data: currentPageData,
    isLoading: isLoadingQuery,
    isFetching: isFetchingQuery,
    isUninitialized: isUninitializedQuery,
    error,
  } = result

  const trigger = useCallback(
    async (arg: TArg): Promise<TResult | undefined> => {
      try {
        isTriggered.current = true
        pendingFetchRef.current = true

        const req = _trigger(arg)
        abortRef.current = () => req.abort()

        return await req.unwrap()
      } catch {
        pendingFetchRef.current = false
      }

      return undefined
    },
    [_trigger],
  )

  const initialTrigger = useCallback(async () => {
    const data = await trigger(initialArg)

    if (Array.isArray(data) && data.length === 0) {
      setHasNextPage(false)
    }
  }, [trigger, initialArg])

  const triggerPage = useCallback(
    async (nextArg: TArg, shouldAppendArg = true) => {
      if (!shouldAppendArg) {
        await trigger(nextArg)
      } else {
        const prevArgs = args.current
        args.current = [...prevArgs, nextArg]

        const data = await trigger(nextArg)

        if (!data) {
          args.current = prevArgs
        }
      }
    },
    [trigger],
  )

  useEffect(() => {
    if (!hasInitialData && !skip) {
      void initialTrigger()
    }
  }, [])

  useDeepCompareDidUpdate(() => {
    if (!skip) {
      reset()
    }
  }, [skip, initialArg])

  useEffect(() => {
    if (currentPageData && withLocalState) {
      const isNewPage = pendingFetchRef.current
      const isSameData = processedDataRef.current === currentPageData

      if (isSameData && !isNewPage) {
        return
      }

      processedDataRef.current = currentPageData

      setItems(
        (prevItems) =>
          mergeItems(
            getItemKey,
            prevItems,
            currentPageData,
            isNewPage,
          ) as TResult,
      )

      if (isNewPage) {
        pendingFetchRef.current = false
      }
    }
  }, [currentPageData, withLocalState])

  useEffect(() => {
    if (!isFetchingQuery) {
      isTriggered.current = false
    }
  }, [isFetchingQuery])

  const fetchNextPage = useCallback(async () => {
    const currentData = currentPageData || initialData

    if (!skip && !isTriggered.current && currentArg && currentData) {
      const nextArg = getNextArgRef.current(currentData, currentArg)

      if (nextArg !== null) {
        await triggerPage(nextArg)
      } else {
        setHasNextPage(false)
      }
    }
  }, [skip, initialData, currentPageData, triggerPage])

  const fetchPage = useCallback(
    async (getArgsCallback: GetArgsCallbackType<TResult, TArg>) => {
      const currentData = currentPageData || initialData

      if (!skip && !isTriggered.current && currentArg && currentData) {
        const nextArg = getArgsCallback(currentData, currentArg)

        if (nextArg === null) {
          return
        }

        await triggerPage(nextArg, !isEqual(nextArg, currentArg))
      }
    },
    [skip, initialData, currentPageData, triggerPage],
  )

  const reset = () => {
    abortRef.current?.()
    args.current = [initialArg]
    pendingFetchRef.current = false
    processedDataRef.current = null

    if (withLocalState) {
      setItems(undefined)
    }

    setHasNextPage(true)

    setTimeout(() => {
      if (!skip) {
        void initialTrigger()
      }
    })
  }

  const isLoading =
    !skip &&
    ((!hasInitialData && isUninitializedQuery) ||
      (!hasInitialData && withLocalState && !items && !error) ||
      (isLoadingQuery && args.current.length === 1) ||
      (isFetchingQuery && args.current.length === 1))

  const isFetchingNext = isFetchingQuery && args.current.length > 1

  return {
    ...(withLocalState && { items: (items || []) as TResult }),
    error,
    isLoading,
    isFetchingNext,
    fetchNextPage,
    hasNextPage,
    fetchPage,
    reset,
  }
}

export default useInfiniteLazyQuery
